import { Col, Row, useTranslation } from '@packages/utils';
import { TColumnType } from 'components/Base/Table/Table.types';
import { TElcMeetingActionItemsUI, TElcDetailUI } from 'data/types/elcs.types';
import { Icon, EIconNames } from 'components/Base/Icon';
import { useTheme } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { RoutesPaths } from 'routes/Routes.types';
import RemoveActionItem from './RemoveActionItem';
import ActionItemTitle from './ActionItemTitle';
import ActionItemDueDate from './ActionItemDueDate';
import ActionItemOwner from './ActionItemOwner';
import ActionItemStatus from './ActionItemStatus';
import { TAddNewRow } from './ActionItemsTab.types';

const useGetData = (
  setNewRowData: React.Dispatch<React.SetStateAction<TAddNewRow | undefined>>,
  elcId?: TElcDetailUI['elcId'],
  // isEditable?: boolean,
) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.elcs.detail.meetings-tab.details.action-items.table.title',
  });

  const handleNavigateToActionPlan = (actionPlanId?: number) => {
    if (!elcId) return;

    // Determine which route to use based on current path
    const isMyElc = location.pathname.includes('/my-elc/');
    const actionPlanRoute = isMyElc
      ? RoutesPaths.MYELCS_ACTION_PLAN
      : RoutesPaths.ELC_ACTION_PLAN;

    // Navigate to the action plan page
    navigate(`/app/${actionPlanRoute.replace(':id', elcId)}`, {
      state: { actionPlanId },
    });
  };

  const defaultColumns: TColumnType<TElcMeetingActionItemsUI>[] = [
    {
      title: t('TASK'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record: TElcMeetingActionItemsUI) => (
        <div
          style={{ pointerEvents: record.actionPlanTaskId ? 'none' : 'auto' }}
          data-cy="meeting_task_name"
        >
          <ActionItemTitle
            {...record}
            elcId={elcId}
            setNewRowData={setNewRowData}
          />
        </div>
      ),
      onCell: () => ({
        style: { minWidth: '250px', maxWidth: '250px' },
      }),
    },
    {
      title: t('DUE_DATE'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (text, record: TElcMeetingActionItemsUI) => (
        <div
          style={{ pointerEvents: record.actionPlanTaskId ? 'none' : 'auto' }}
          data-cy="meeting_task_due_date"
        >
          <ActionItemDueDate
            {...record}
            elcId={elcId}
            setNewRowData={setNewRowData}
          />
        </div>
      ),
      onCell: () => ({
        style: { minWidth: '200px' },
      }),
    },
    {
      title: t('OWNER'),
      dataIndex: 'ownerId',
      key: 'ownerId',
      render: (text, record: TElcMeetingActionItemsUI) => (
        <div
          style={{ pointerEvents: record.actionPlanTaskId ? 'none' : 'auto' }}
          data-cy="meeting_task_responsible"
        >
          <ActionItemOwner
            {...record}
            elcId={elcId}
            setNewRowData={setNewRowData}
          />
        </div>
      ),
      onCell: () => ({
        style: { minWidth: '200px' },
      }),
    },
    {
      title: t('ACTIONS'),
      key: 'actions',
      dataIndex: 'actions',
      width: 120,
      onCell: () => ({
        style: { minWidth: '120px' },
        onClick: e => {
          e.stopPropagation();
        },
      }),
      render: (text, record: TElcMeetingActionItemsUI) => (
        <div style={{ pointerEvents: 'auto' }} data-cy="meeting_task_actions">
          <Row gutter={[8, 8]} align="middle" justify="center">
            <Col>
              <ActionItemStatus
                {...record}
                elcId={elcId}
                setNewRowData={setNewRowData}
              />
            </Col>
            <Col>
              <RemoveActionItem elcId={elcId} {...record} />
            </Col>
            <Col style={{ minWidth: '32px', textAlign: 'center' }}>
              {record.actionPlanTaskId && elcId && (
                <button
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleNavigateToActionPlan(record.actionPlanId);
                  }}
                  style={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    display: 'inline-block',
                  }}
                  aria-label="View in Action Plan"
                  data-cy="meeting_task_view_in_action_plan"
                >
                  <Icon
                    icon={EIconNames.EYE}
                    color={theme.colorBlackIconColor}
                    tooltip={{ title: 'View in Action Plan' }}
                  />
                </button>
              )}
            </Col>
          </Row>
        </div>
      ),
    },
  ];
  return { defaultColumns };
};

export default useGetData;
